import React, { useEffect, useRef, useState, useCallback } from 'react';
import { chatStyles } from '@zonofi/chats-common';
import { FileParams, theme } from '@zonofi/common';
import { ActivityIndicator } from 'react-native-paper';
import Icon from 'react-native-easy-icon';
import { SHARED_SVGS } from '@zonofi/shared';
import './video-view.scss';
import {
  dispatch,
  MultiToastType,
  updateActionToastData,
} from '@zonofi/seller-rtk-store';
import { useMediaHandler } from '../../helpers/handle-media';

export const VideoView: React.FC<FileParams> = (props) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const hasRetriedRef = useRef(false);
  const permanentFailureRef = useRef(false);
  const isRetryingRef = useRef(false);

  const [didFail, setDidFail] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const { handleMedia, isLoading } = useMediaHandler({
    ...props,
    maxRetries: 0,
    onPlay: async (url: string) => {
      const video = videoRef.current;
      if (!video) return;

      if (didFail && permanentFailureRef.current) return;

      video.pause();
      video.src = url;
      video.load();

      await new Promise<void>((resolve) => {
        const onCanPlay = () => {
          video.removeEventListener('canplay', onCanPlay);
          resolve();
        };
        video.addEventListener('canplay', onCanPlay);
      });

      try {
        await video.play();
        setIsPlaying(true);

        hasRetriedRef.current = false;
        isRetryingRef.current = false;
        permanentFailureRef.current = false;
        setDidFail(false);
      } catch (err) {
        console.warn('video.play() failed:', err);
        isRetryingRef.current = false;
      }
    },
  });

  const togglePlayPause = async () => {
    const video = videoRef.current;
    if (!video) return;

    // Fetch media again if failed or no src
    if (!video.src || didFail) {
      if (permanentFailureRef.current) {
        permanentFailureRef.current = false;
        hasRetriedRef.current = false;
      }

      setDidFail(false);
      await handleMedia('open');
      return;
    }

    try {
      if (video.paused) {
        await video.play();
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }
    } catch (err) {
      console.warn('toggle video failed:', err);
    }
  };

  const download = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    await handleMedia('download');
  };

  // ✅ Error handler uses same retry strategy as AudioBubble
  const handleVideoError = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    if (!video.src) return;

    if (permanentFailureRef.current) return;

    if (isRetryingRef.current) return;

    const clearState = () => {
      video.pause();
      video.src = '';
      video.load();
    };

    // 🔁 Retry once
    if (!hasRetriedRef.current) {
      hasRetriedRef.current = true;
      isRetryingRef.current = true;
      setDidFail(true);

      console.warn('Video failed — retrying once...');

      clearState();

      setTimeout(async () => {
        try {
          await handleMedia('open', true);
        } catch (err) {
          console.error('Retry handleMedia failed:', err);
        } finally {
          isRetryingRef.current = false;
        }
      }, 300);

      return;
    }

    // ❌ Permanent failure
    console.error('Video failed again — permanent failure.');

    permanentFailureRef.current = true;
    isRetryingRef.current = false;
    setDidFail(true);

    clearState();
    video.onerror = null;

    dispatch(
      updateActionToastData({
        visibility: true,
        title: 'Video Playback Error',
        description:
          'Unable to play this video file. Tap the play button to try again.',
        type: MultiToastType.ERROR,
      })
    );
  }, [handleMedia]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return () => {};

    const handleEnded = () => setIsPlaying(false);

    video.onerror = handleVideoError;
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('ended', handleEnded);
      video.onerror = null;
    };
  }, [handleVideoError]);

  return (
    <div className="video-wrapper" onClick={togglePlayPause}>
      <video
        ref={videoRef}
        className="template-video"
        preload="metadata"
        controls={false}
      />

      {!isPlaying && (
        <div className="play-overlay">
          <Icon name="play" type="feather" size={36} color="#fff" />
        </div>
      )}

      {!isLoading && (
        <button
          className="download-button"
          onClick={download}
          disabled={isLoading}
        >
          {isLoading ? (
            <ActivityIndicator size={20} color={theme.text.secondary} />
          ) : (
            <SHARED_SVGS.DownloadComponent className="custom-download-icon" />
          )}
        </button>
      )}
    </div>
  );
};
